import { Component, ElementRef, HostListener, Input, OnDestroy } from '@angular/core';
import { ErgFeedbackService } from './erg-feedback.service';
import { ErgFeedbackPayload, ErgFeedbackResponse, ErgFeedbackType } from './erg-feedback.model';

type DialogView = 'form' | 'success' | 'responses' | 'discard';

interface FeedbackTypeOption {
  value: ErgFeedbackType;
  label: string;
  icon: string;
}

@Component({
  selector: 'app-erg-feedback',
  templateUrl: './erg-app-feedback.component.html',
  styleUrls: ['./erg-app-feedback.component.css']
})
export class ErgAppFeedbackComponent implements OnDestroy {

  @Input() isAdmin = false;
  @Input() userId = '';

  isOpen = false;
  view: DialogView = 'form';
  isSubmitting = false;
  submitAttempted = false;
  errorMessage = '';

  experienceRating = 0;
  feedbackType: ErgFeedbackType | null = null;
  feedbackDetails = '';

  readonly ratingOptions = [
    { value: 1, label: 'Very poor' },
    { value: 2, label: 'Poor' },
    { value: 3, label: 'Okay' },
    { value: 4, label: 'Good' },
    { value: 5, label: 'Excellent' }
  ];

  readonly feedbackTypeOptions: FeedbackTypeOption[] = [
    { value: 'Issue', label: 'Issue', icon: 'fa-exclamation-circle' },
    { value: 'Idea', label: 'Idea', icon: 'fa-lightbulb-o' },
    { value: 'Other', label: 'Other', icon: 'fa-comment-o' }
  ];

  isLoadingResponses = false;
  responsesError = '';

  responses: ErgFeedbackResponse[] = [];

  private previouslyFocused: HTMLElement | null = null;

  private returnFocusId = '';
  private originalBodyOverflow = '';
  private pageScrollLocked = false;

  constructor(
    private readonly feedbackService: ErgFeedbackService,
    private readonly elementRef: ElementRef
  ) { }

  ngOnDestroy(): void {
    this.unlockPageScroll();
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.isOpen) {
      this.requestClose();
    }
  }

  onOverlayClick(event: MouseEvent): void {
    if (event.target === event.currentTarget) {
      this.requestClose();
    }
  }

  open(): void {
    this.resetForm();
    this.view = 'form';
    this.openDialog();
  }

  openResponses(): void {
    if (!this.isAdmin) { return; }

    this.view = 'responses';
    this.openDialog();
    this.loadResponses();
  }

  loadResponses(): void {
    if (!this.isAdmin) { return; }

    this.isLoadingResponses = true;
    this.responsesError = '';
    this.responses = [];
    this.feedbackService.getFeedback().subscribe(
      (data: ErgFeedbackResponse[]) => {
        this.responses = data || [];
        this.isLoadingResponses = false;
      },
      (error: any) => {
        this.isLoadingResponses = false;
        this.responsesError = 'Could not load feedback responses. Please try again.';
        console.error('Error loading app feedback:', error);
      }
    );
  }

  requestClose(): void {
    if (!this.isOpen || this.isSubmitting) { return; }

    if (this.view === 'discard') {
      this.keepEditing();
      return;
    }

    if (this.view === 'form' && this.isDirty) {
      const activeElement = document.activeElement as HTMLElement;
      this.returnFocusId = activeElement && activeElement.id ? activeElement.id : '';
      this.view = 'discard';
      this.focusAfterRender('.erg-fb-discard-keep');
      return;
    }

    this.closeDialog();
  }

  keepEditing(): void {
    this.view = 'form';
    this.focusAfterRender(this.returnFocusId ? '#' + this.returnFocusId : '#ergFbDetails');
  }

  discardFeedback(): void {
    this.resetForm();
    this.closeDialog();
  }

  startNewFeedback(): void {
    this.resetForm();
    this.view = 'form';
    this.focusAfterRender('#ergFbRating1');
  }

  get isDirty(): boolean {
    return this.experienceRating > 0 || this.feedbackType !== null || this.feedbackDetails.length > 0;
  }

  get ratingIsValid(): boolean {
    return Number.isInteger(this.experienceRating) && this.experienceRating >= 1 && this.experienceRating <= 5;
  }

  get feedbackTypeIsValid(): boolean {
    return this.feedbackTypeOptions.some((option) => option.value === this.feedbackType);
  }

  get detailsAreValid(): boolean {
    const detailLength = this.feedbackDetails.trim().length;
    return detailLength >= 1 && detailLength <= 500;
  }

  get formIsValid(): boolean {
    return this.ratingIsValid && this.feedbackTypeIsValid && this.detailsAreValid;
  }

  get detailsPlaceholder(): string {
    switch (this.feedbackType) {
      case 'Issue':
        return 'What happened, and what were you trying to do?';
      case 'Idea':
        return 'What would make ERG Connect work better for you?';
      default:
        return 'Tell us what you would like us to know.';
    }
  }

  submit(): void {
    if (this.isSubmitting) { return; }

    this.submitAttempted = true;
    if (!this.formIsValid) {
      this.focusFirstInvalidControl();
      return;
    }

    const userId = this.userId.trim();
    if (!userId) {
      this.errorMessage = 'We could not identify your account. Please refresh the page and try again.';
      this.focusAfterRender('#ergFbFormError');
      return;
    }

    this.isSubmitting = true;
    this.errorMessage = '';

    const payload: ErgFeedbackPayload = {
      experienceRating: this.experienceRating,
      feedbackType: this.feedbackType as ErgFeedbackType,
      feedbackDetails: this.feedbackDetails.trim(),
      userId
    };

    this.feedbackService.saveFeedback(payload).subscribe(
      () => {
        this.isSubmitting = false;
        this.submitAttempted = false;
        this.view = 'success';
        this.focusAfterRender('#ergFbSuccessClose');
      },
      (error: any) => {
        this.isSubmitting = false;
        this.errorMessage = 'Something went wrong while saving your feedback. Please try again.';
        this.focusAfterRender('#ergFbFormError');
        console.error('Error saving app feedback:', error);
      }
    );
  }

  ratingLabel(value: number): string {
    const option = this.ratingOptions.find((rating) => rating.value === value);
    return option ? option.label : 'Not rated';
  }

  trackResponse(_index: number, response: ErgFeedbackResponse): number {
    return response.feedbackId;
  }

  private openDialog(): void {
    this.previouslyFocused = document.activeElement as HTMLElement;
    this.isOpen = true;
    this.lockPageScroll();
  }

  private closeDialog(): void {
    this.isOpen = false;
    this.unlockPageScroll();
    if (this.previouslyFocused && typeof this.previouslyFocused.focus === 'function') {
      this.previouslyFocused.focus();
    }
  }

  private focusFirstInvalidControl(): void {
    if (!this.ratingIsValid) {
      this.focusAfterRender('#ergFbRating1');
    } else if (!this.feedbackTypeIsValid) {
      this.focusAfterRender('#ergFbTypeIssue');
    } else {
      this.focusAfterRender('#ergFbDetails');
    }
  }

  private focusAfterRender(selector: string): void {
    setTimeout(() => {
      const element = this.elementRef.nativeElement.querySelector(selector) as HTMLElement;
      if (element) {
        element.focus();
      }
    });
  }

  private lockPageScroll(): void {
    if (this.pageScrollLocked) { return; }

    this.originalBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    this.pageScrollLocked = true;
  }

  private unlockPageScroll(): void {
    if (!this.pageScrollLocked) { return; }

    document.body.style.overflow = this.originalBodyOverflow;
    this.pageScrollLocked = false;
  }

  private resetForm(): void {
    this.isSubmitting = false;
    this.submitAttempted = false;
    this.errorMessage = '';
    this.experienceRating = 0;
    this.feedbackType = null;
    this.feedbackDetails = '';
    this.returnFocusId = '';
  }
}
